/**
 * US Tax Residency Component
 *
 * This component handles the collection of US tax residency information,
 * which is required for FATCA (Foreign Account Tax Compliance Act) compliance.
 * It determines if a person is a US citizen or tax resident and collects
 * their US Tax Identification Number (TIN) if applicable.
 */

import { type ReactElement, useState, useCallback } from 'react';
import { RadioGroupBoolean, RadioButtonBoolean, TextField } from '@repo/design-system-kit';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import styles from './tax-residency.module.css';

const LABEL_ID = 'us-tax-residency-label';

export interface UsTaxResidencyData {
  isTaxResident?: boolean;
  taxNumber?: string;
}

export interface UsTaxResidencyProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  value?: UsTaxResidencyData;
  initialData?: Partial<UsTaxResidencyData>;
  onChange: (data: UsTaxResidencyData) => void;
  isRequired?: boolean;
  label: string;
  showErrors?: boolean;
}

export function UsTaxResidency({
  className,
  value,
  initialData,
  onChange,
  label,
  isRequired = false,
  showErrors = false,
  ...props
}: UsTaxResidencyProps): ReactElement {
  const { formatMessage } = useIntl();
  const [internalData, setInternalData] = useState<UsTaxResidencyData>(() => ({
    ...initialData,
  }));

  // Use value prop if provided (controlled), otherwise use internal state (uncontrolled)
  const formData = value !== undefined ? value : internalData;
  const showConditionalFields = formData.isTaxResident;

  const updateFormData = useCallback(
    (updates: Partial<UsTaxResidencyData>): void => {
      const newData: UsTaxResidencyData = { ...formData, ...updates };
      // Update internal state only if not controlled
      if (value === undefined) {
        setInternalData(newData);
      }
      onChange(newData);
    },
    [formData, onChange, value]
  );

  const handleTaxResidencyChange = useCallback(
    (isTaxResident: boolean): void => {
      const updates: Partial<UsTaxResidencyData> = { isTaxResident };

      if (!isTaxResident) {
        updates.taxNumber = undefined;
      }

      updateFormData(updates);
    },
    [updateFormData]
  );

  const handleTaxNumberChange = useCallback(
    (taxNumber: string): void => {
      updateFormData({ taxNumber: taxNumber.trim() || undefined });
    },
    [updateFormData]
  );

  return (
    <div
      className={cx(styles.wrapper, className)}
      data-testid="us-tax-residency-section"
      {...props}
    >
      <div className={styles.sectionWrapper}>
        <h2 className="body-standard" id={LABEL_ID}>
          {label}
        </h2>
        <p className="caption">
          <FormattedMessage id="customer-registration-kit.tax-residency-country.us-citizen.description" />
        </p>
      </div>

      <div className={styles.sectionWrapper}>
        <RadioGroupBoolean
          aria-labelledby={LABEL_ID}
          errorMessage={
            showErrors && isRequired && typeof formData.isTaxResident !== 'boolean'
              ? formatMessage({
                  id: 'customer-registration-kit.generic.required-field.error',
                })
              : undefined
          }
          isInvalid={Boolean(
            showErrors && isRequired && typeof formData.isTaxResident !== 'boolean'
          )}
          isRequired={isRequired}
          onChange={handleTaxResidencyChange}
          value={formData.isTaxResident}
        >
          <RadioButtonBoolean data-testid="us-tax-residency-yes" value>
            {formatMessage({ id: 'customer-registration-kit.common.yes' })}
          </RadioButtonBoolean>

          <RadioButtonBoolean data-testid="us-tax-residency-no" value={false}>
            {formatMessage({ id: 'customer-registration-kit.common.no' })}
          </RadioButtonBoolean>
        </RadioGroupBoolean>
      </div>

      {showConditionalFields ? (
        <div className={styles.conditionalFields}>
          <TextField
            data-testid="us-tax-residency-tin"
            errorMessage={
              showErrors && isRequired && !formData.taxNumber?.trim()
                ? formatMessage({
                    id: 'customer-registration-kit.generic.required-field.error',
                  })
                : undefined
            }
            isRequired
            label={formatMessage({
              id: 'customer-registration-kit.tax-residency-country.us-tin',
            })}
            onChange={handleTaxNumberChange}
            placeholder={formatMessage({
              id: 'customer-registration-kit.foreign-tin.placeholder',
            })}
            value={formData.taxNumber ?? ''}
          />
        </div>
      ) : null}
    </div>
  );
}
